import { c as _c } from "react/compiler-runtime";
/* eslint-disable react/jsx-props-no-spreading */
import type { Color } from '@onefootprint/design-tokens';
import type { Icon } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributeAnchorTarget, ReactElement, Ref } from 'react';
import { forwardRef } from 'react';
import LoadingSpinner from '../loading-spinner';
import type { ButtonSize, ButtonVariant } from './button.types';
type BaseProps = {
  children: string | ReactElement<Icon>;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  iconColor?: Color;
  loading?: boolean;
  loadingAriaLabel?: string;
  prefixIcon?: Icon;
  suffixIcon?: Icon;
  size?: ButtonSize;
  testID?: string;
  variant?: ButtonVariant;
};
export type ButtonAsButtonProps = BaseProps & {
  href?: never;
  target?: never;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'form'>;
export type ButtonAsAnchorProps = BaseProps & {
  /** Renders a real anchor, so cmd-click, right-click and hover preview all work. */
  href: string;
  target?: HTMLAttributeAnchorTarget;
  /** Button-only attributes are rejected here: an anchor submits nothing. */
  type?: never;
  form?: never;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'type'>;
export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;
const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((t0, ref) => {
  const $ = _c(66);
  let PrefixIcon;
  let SuffixIcon;
  let children;
  let className;
  let form;
  let fullWidth;
  let href;
  let iconColor;
  let loadingAriaLabel;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let target;
  let testID;
  if ($[0] !== t0) {
    ({
      children,
      className,
      disabled: t1,
      form,
      fullWidth,
      href,
      iconColor,
      loading: t2,
      loadingAriaLabel,
      prefixIcon: PrefixIcon,
      suffixIcon: SuffixIcon,
      size: t3,
      target,
      testID,
      type: t4,
      variant: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = PrefixIcon;
    $[2] = SuffixIcon;
    $[3] = children;
    $[4] = className;
    $[5] = form;
    $[6] = fullWidth;
    $[7] = href;
    $[8] = iconColor;
    $[9] = loadingAriaLabel;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = target;
    $[17] = testID;
  } else {
    PrefixIcon = $[1];
    SuffixIcon = $[2];
    children = $[3];
    className = $[4];
    form = $[5];
    fullWidth = $[6];
    href = $[7];
    iconColor = $[8];
    loadingAriaLabel = $[9];
    props = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    target = $[16];
    testID = $[17];
  }
  const disabled = t1 === undefined ? false : t1;
  const loading = t2 === undefined ? false : t2;
  const size = t3 === undefined ? "default" : t3;
  const type = t4 === undefined ? "button" : t4;
  const variant = t5 === undefined ? "primary" : t5;
  const defaultIconColor = variant === "primary" ? "quinary" : "primary";
  let t6;
  if ($[18] !== PrefixIcon || $[19] !== SuffixIcon || $[20] !== children || $[21] !== defaultIconColor || $[22] !== iconColor || $[23] !== loading) {
    t6 = () => <span className={`flex items-center justify-center gap-2 ${loading ? "invisible" : "visible"}`}>{PrefixIcon && <PrefixIcon color={iconColor || defaultIconColor} />}<span className="overflow-hidden text-ellipsis whitespace-nowrap">{children}</span>{SuffixIcon && <SuffixIcon color={iconColor || defaultIconColor} />}</span>;
    $[18] = PrefixIcon;
    $[19] = SuffixIcon;
    $[20] = children;
    $[21] = defaultIconColor;
    $[22] = iconColor;
    $[23] = loading;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const getContent = t6;
  let t7;
  if ($[25] !== className) {
    t7 = cx("fp-button fp-custom-appearance text-label-2", className);
    $[25] = className;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== fullWidth || $[28] !== loading || $[29] !== size || $[30] !== t7 || $[31] !== testID || $[32] !== variant) {
    t8 = {
      className: t7,
      "data-full-width": fullWidth,
      "data-loading": loading,
      "data-size": size,
      "data-testid": testID,
      "data-variant": variant,
      tabIndex: 0
    };
    $[27] = fullWidth;
    $[28] = loading;
    $[29] = size;
    $[30] = t7;
    $[31] = testID;
    $[32] = variant;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  const sharedProps = t8;
  let t9;
  if ($[34] !== getContent) {
    t9 = getContent();
    $[34] = getContent;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const t10 = loading ? "visible" : "invisible";
  let t11;
  if ($[36] !== t10) {
    t11 = cx("absolute", t10);
    $[36] = t10;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const t12 = !loading;
  const t13 = variant === "primary" ? "quinary" : "tertiary";
  let t14;
  if ($[38] !== loadingAriaLabel || $[39] !== t13) {
    t14 = <LoadingSpinner ariaLabel={loadingAriaLabel} color={t13} />;
    $[38] = loadingAriaLabel;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t11 || $[42] !== t12 || $[43] !== t14) {
    t15 = <div className={t11} aria-hidden={t12}>{t14}</div>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t15 || $[46] !== t9) {
    t16 = <>{t9}{t15}</>;
    $[45] = t15;
    $[46] = t9;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const content = t16;
  if (href) {
    const t17 = props as AnchorHTMLAttributes<HTMLAnchorElement>;
    const t18 = disabled || loading || undefined;
    const t19 = disabled || loading ? undefined : href;
    const t20 = ref as Ref<HTMLAnchorElement>;
    const t21 = target === "_blank" ? "noopener noreferrer" : undefined;
    const t22 = disabled || loading ? -1 : 0;
    let t23;
    if ($[48] !== content || $[49] !== sharedProps || $[50] !== t17 || $[51] !== t18 || $[52] !== t19 || $[53] !== t20 || $[54] !== t21 || $[55] !== t22 || $[56] !== target) {
      t23 = <a {...t17} {...sharedProps} aria-disabled={t18} href={t19} ref={t20} rel={t21} tabIndex={t22} target={target}>{content}</a>;
      $[48] = content;
      $[49] = sharedProps;
      $[50] = t17;
      $[51] = t18;
      $[52] = t19;
      $[53] = t20;
      $[54] = t21;
      $[55] = t22;
      $[56] = target;
      $[57] = t23;
    } else {
      t23 = $[57];
    }
    return t23;
  }
  const t17 = props as ButtonHTMLAttributes<HTMLButtonElement>;
  const t18 = ref as Ref<HTMLButtonElement>;
  let t19;
  if ($[58] !== content || $[59] !== disabled || $[60] !== form || $[61] !== sharedProps || $[62] !== t17 || $[63] !== t18 || $[64] !== type) {
    t19 = <button {...t17} {...sharedProps} disabled={disabled} form={form} ref={t18} type={type}>{content}</button>;
    $[58] = content;
    $[59] = disabled;
    $[60] = form;
    $[61] = sharedProps;
    $[62] = t17;
    $[63] = t18;
    $[64] = type;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  return t19;
});
export default Button;